/**
 * Base error class for all GPT Platform SDK errors
 */
export class GptCoreError extends Error {
  public readonly name: string;
  public readonly statusCode?: number;
  public readonly code?: string;
  public readonly requestId?: string;
  public readonly headers?: Record<string, string>;
  public readonly body?: unknown;
  public cause?: Error;

  constructor(
    message: string,
    options?: {
      statusCode?: number;
      code?: string;
      requestId?: string;
      headers?: Record<string, string>;
      body?: unknown;
      cause?: Error;
    },
  ) {
    super(message);
    this.name = this.constructor.name;
    this.statusCode = options?.statusCode;
    this.code = options?.code;
    this.requestId = options?.requestId;
    this.headers = options?.headers;
    this.body = options?.body;
    this.cause = options?.cause;

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }
}

/**
 * Authentication errors (401)
 */
export class AuthenticationError extends GptCoreError {
  constructor(
    message = "Authentication failed",
    options?: ConstructorParameters<typeof GptCoreError>[1],
  ) {
    super(message, { statusCode: 401, ...options });
  }
}

/**
 * Authorization/Permission errors (403)
 */
export class AuthorizationError extends GptCoreError {
  constructor(
    message = "Permission denied",
    options?: ConstructorParameters<typeof GptCoreError>[1],
  ) {
    super(message, { statusCode: 403, ...options });
  }
}

/**
 * Resource not found errors (404)
 */
export class NotFoundError extends GptCoreError {
  constructor(
    message = "Resource not found",
    options?: ConstructorParameters<typeof GptCoreError>[1],
  ) {
    super(message, { statusCode: 404, ...options });
  }
}

/**
 * Validation errors (400, 422)
 */
export class ValidationError extends GptCoreError {
  public readonly errors?: Array<{
    field?: string;
    message: string;
  }>;

  constructor(
    message = "Validation failed",
    errors?: Array<{ field?: string; message: string }>,
    options?: ConstructorParameters<typeof GptCoreError>[1],
  ) {
    super(message, { statusCode: 422, ...options });
    this.errors = errors;
  }
}

/**
 * Rate limiting errors (429)
 */
export class RateLimitError extends GptCoreError {
  public readonly retryAfter?: number;

  constructor(
    message = "Rate limit exceeded",
    retryAfter?: number,
    options?: ConstructorParameters<typeof GptCoreError>[1],
  ) {
    super(message, { statusCode: 429, ...options });
    this.retryAfter = retryAfter;
  }
}

/**
 * Network/connection errors
 */
export class NetworkError extends GptCoreError {
  constructor(
    message = "Network request failed",
    options?: ConstructorParameters<typeof GptCoreError>[1],
  ) {
    super(message, options);
  }
}

/**
 * Timeout errors
 */
export class TimeoutError extends GptCoreError {
  constructor(
    message = "Request timeout",
    options?: ConstructorParameters<typeof GptCoreError>[1],
  ) {
    super(message, options);
  }
}

/**
 * Server errors (500+)
 */
export class ServerError extends GptCoreError {
  constructor(
    message = "Internal server error",
    options?: ConstructorParameters<typeof GptCoreError>[1],
  ) {
    super(message, { statusCode: 500, ...options });
  }
}

/**
 * Parse error response and throw appropriate error class
 */
export function handleApiError(error: unknown): never {
  const err = error as Record<string, unknown>;

  // Extract error details from response - handle generated client structure
  const response = (err?.response || err) as Record<string, unknown>;
  let statusCode = (response?.status || err?.status || err?.statusCode) as
    | number
    | undefined;
  const headers = (response?.headers || err?.headers) as
    | Headers
    | Record<string, string>
    | null
    | undefined;
  const requestId = ((headers as Headers)?.get?.("x-request-id") ||
    (headers as Record<string, string>)?.["x-request-id"]) as
    | string
    | undefined;

  // The body might be in different locations depending on the error structure
  const body = (response?.body ||
    response?.data ||
    err?.body ||
    err?.data ||
    err) as Record<string, unknown> | string | undefined;

  // Try to extract error message from JSON:API error format
  let message = "An error occurred";
  let errors: Array<{ field?: string; message: string }> | undefined;

  const bodyObj = body as Record<string, unknown> | undefined;
  if (bodyObj?.errors && Array.isArray(bodyObj.errors)) {
    // JSON:API error format (errors is an array)
    const firstError = bodyObj.errors[0] as
      | { title?: string; detail?: string; status?: string }
      | undefined;
    message = firstError?.title || firstError?.detail || message;
    errors = (
      bodyObj.errors as Array<{
        source?: { pointer?: string };
        detail?: string;
        title?: string;
      }>
    ).map((e) => ({
      field: e.source?.pointer?.split("/").pop(),
      message: e.detail || e.title || "Unknown error",
    }));

    // Recover HTTP status from JSON:API per-error "status" field
    if (!statusCode && firstError?.status) {
      const parsed = parseInt(firstError.status, 10);
      if (!isNaN(parsed)) statusCode = parsed;
    }
  } else if (
    bodyObj?.errors &&
    typeof bodyObj.errors === "object" &&
    !Array.isArray(bodyObj.errors)
  ) {
    // Non-standard error format: { errors: { detail: "..." } }
    const errObj = bodyObj.errors as Record<string, unknown>;
    message = (errObj.detail as string) || (errObj.title as string) || message;
  } else if (bodyObj?.message) {
    message = bodyObj.message as string;
  } else if (typeof body === "string") {
    message = body;
  } else if (err?.message) {
    message = err.message as string;
  }

  // Security: Filter sensitive headers to prevent information disclosure
  const sensitiveHeaderPatterns = [
    "set-cookie",
    "authorization",
    "x-application-key",
    "cookie",
    "x-forwarded-for",
    "x-real-ip",
  ];

  const filterSensitiveHeaders = (
    hdrs: Headers | Record<string, string> | null | undefined,
  ): Record<string, string> | undefined => {
    if (!hdrs) return undefined;

    const entries: [string, string][] =
      hdrs instanceof Headers
        ? Array.from(hdrs.entries())
        : Object.entries(hdrs);

    const filtered = entries.filter(([key]) => {
      const lowerKey = key.toLowerCase();
      return !sensitiveHeaderPatterns.some((pattern) =>
        lowerKey.includes(pattern),
      );
    });

    return filtered.length > 0 ? Object.fromEntries(filtered) : undefined;
  };

  const errorOptions = {
    statusCode,
    requestId,
    headers: filterSensitiveHeaders(headers),
    body,
    cause: error instanceof Error ? error : undefined,
  };

  // Throw appropriate error based on status code
  switch (statusCode) {
    case 401:
      throw new AuthenticationError(message, errorOptions);
    case 403:
      throw new AuthorizationError(message, errorOptions);
    case 404:
      throw new NotFoundError(message, errorOptions);
    case 400:
    case 422:
      throw new ValidationError(message, errors, errorOptions);
    case 429: {
      const retryAfter =
        (headers as Headers)?.get?.("retry-after") ||
        (headers as Record<string, string>)?.["retry-after"];
      throw new RateLimitError(
        message,
        retryAfter ? parseInt(retryAfter, 10) : undefined,
        errorOptions,
      );
    }
    case 500:
    case 502:
    case 503:
    case 504:
      throw new ServerError(message, errorOptions);
    default:
      if (statusCode && statusCode >= 400) {
        throw new GptCoreError(message, errorOptions);
      }
      // Network/connection errors
      throw new NetworkError(message, errorOptions);
  }
}
